import './assets/main.css'
import * as Sentry from '@sentry/vue'
import { createApp } from 'vue'
import { pinia } from './modules/pinia.ts'
import vuetify from './plugins/vuetify'
import router from './router'

import { AuthenticationManagement } from '@haskoning/vue-authentication-middleware'
import { UserManagementApp } from '@haskoning/vue-user-management'
import {
  OpengroundPlugin,
  setSpeckleConfig,
  useSpeckleStore,
  VueSpecklePlugin
} from '@haskoning/vue-speckle'
import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import App from './App.vue'
import 'vuetify/dist/vuetify.min.css'

import HeaderImage from '/header.jpeg'
import HeaderImageLazy from '/header_lazy.jpg'

import apiUrls from '@/api/apiUrls.js'
import { useDjangoAuthStore } from './stores/authDjango'

import { useSnackbarStore } from './stores/snackbarStore'
import { opengroundAdapter } from './adapters/openground'

pinia.use(({ store }) => {
  store.$api = api
  store.$apiUrls = apiUrls
})
const { bus } = useEventsBus()

const { api } = useApi()

const app = createApp(App)
  .use(vuetify)
  .use(pinia)
  .use(OpengroundPlugin, {
    router,
    callbackURL: import.meta.env.VITE_APP_OPENGROUND_CALLBACK_URL,
    clientId: import.meta.env.VITE_APP_OPENGROUND_CLIENT_ID,
    backendAdapter: opengroundAdapter
  })
  .use(router)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ['*'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: 'Feedback',
      formTitle: 'Send Feedback',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      messagePlaceholder: 'What would you like to tell us?',
      successMessageText: 'Thank you for your feedback!',
      colorScheme: 'light',
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false
    })
  ],
  ignoreErrors: [
    // Leaflet's zoom animation schedules a transitionend fallback timer that Map.remove() does not
    // cancel, so it fires on a destroyed map when the user navigates away mid-zoom. Nothing
    // user-visible breaks: the map is already gone by then.
    // Fixed upstream in Leaflet 2.0 (Leaflet#9575, PR Leaflet#9577: remove() now clears the timer),
    // but not backported to 1.x. Drop this once we can move off leaflet 1.9.4 — that upgrade is
    // blocked by @vue-leaflet/vue-leaflet (peer-deps leaflet ^1.6.0), leaflet-draw and
    // leaflet-contextmenu, none of which support Leaflet 2 yet.
    /_leaflet_pos/
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

const authStore = useDjangoAuthStore(pinia)

app
  .use(AuthenticationManagement, {
    appTitle: 'GEOProfile',
    headerImage: HeaderImage,
    appSubTitle: false,
    headerImageLazy: HeaderImageLazy,
    api: api,
    snackbarStore: useSnackbarStore(pinia),
    authStore: authStore,
    apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL.replace(/\/$/, ''),
    appFrontendBaseUrl: import.meta.env.VITE_APP_FRONTEND_BASE_URL,
    showForgotPassword: false
  })
  .provide('useAuthStore', authStore)

app.use(UserManagementApp, {
  authorizationStore: useAuthorizationStore(),
  authStore: authStore
})

const speckleConfig = {
  serverUrl: import.meta.env.VITE_APP_SERVER_URL,
  appId: import.meta.env.VITE_APP_SPECKLE_ID,
  appSecret: import.meta.env.VITE_APP_SPECKLE_SECRET,
  appName: import.meta.env.VITE_APP_SPECKLE_NAME,
  workspaceId: import.meta.env.VITE_APP_SPECKLE_WORKSPACE_ID
}
setSpeckleConfig(
  speckleConfig.serverUrl,
  speckleConfig.workspaceId,
  speckleConfig.appId,
  speckleConfig.appSecret,
  speckleConfig.appName
)

app.use(ComponentLibrary, { externalBus: bus })
app.use(VueSpecklePlugin, {
  componentsAutoSelectData: true,
  speckleStore: () => useSpeckleStore({ autoSelectData: false })
})

router.isReady().then(() => {
  const feedback = Sentry.getFeedback()
  if (!feedback) {
    return
  }
  let widget: ReturnType<typeof feedback.createWidget> | null = null
  const isAuthenticated = () => {
    return authStore.user !== null
  }
  const updateFeedbackWidget = () => {
    if (isAuthenticated() && !widget) {
      widget = feedback.createWidget()
      widget.appendToDom()
      if (authStore.user?.email) {
        Sentry.setUser({
          username: authStore.user.first_name ?? '',
          email: authStore.user.email ?? ''
        })
      }
    } else if (!isAuthenticated() && widget) {
      widget.removeFromDom()
      widget = null
      Sentry.setUser(null)
    }
  }
  updateFeedbackWidget()
  authStore.$subscribe(updateFeedbackWidget)
})

app.mount('#app')
